"use client"

import { Input } from "@/components/ui/input"
import { SettingsHeader } from "../../../components/SettingsHeader"
import { InputDescription } from "../../components/InputRow/InputDescription"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Blog } from "@types"
import { useCrosspostsLogic } from "../hooks/useCrosspostsLogic"

type CrosspostType = {
  id: string
  blogId: string
  feedUrl: string
  sendNewsletter: boolean
  lastImportedTs: number
}

export type Props = {
  blogId: Blog["id"]
  initialCrosspost: CrosspostType | null
}

export default function Crossposts({ blogId, initialCrosspost }: Props) {
  const {
    isLoading,
    url,
    setUrl,
    sendNewsletter,
    setSendNewsletter,
    previousValues,
    crosspostId,
    handleSubmit,
    handleDelete,
    handleUpdate,
  } = useCrosspostsLogic({ blogId, initialCrosspost })

  return (
    <section className="space-y-6">
      <SettingsHeader
        title="Automatically crosspost your writing to Paragraph"
        description="Share your posts with more people and unlock new ways to earn by automatically crossposting each of your new posts to your Paragraph publication."
      />

      <form className="space-y-4" onSubmit={handleSubmit}>
        <div className="space-y-2 max-w-[530px]">
          <InputDescription>Your publication URL</InputDescription>
          <Input
            value={url}
            disabled={!!previousValues?.feedUrl}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://www.yourblog.com/rss"
          />
          <p className="text-sm text-muted-foreground">
            By connecting your publication, new posts will automatically appear
            on your Paragraph publication when published. You can edit or remove
            any post after publication, and you can turn off this connection
            anytime.
          </p>

          <div className="flex pt-4 space-x-2 items-top">
            <Checkbox
              id="sendNewsletter"
              checked={sendNewsletter}
              onCheckedChange={(isChecked: boolean) =>
                setSendNewsletter(!!isChecked)
              }
            />
            <div className="grid gap-1.5 leading-none">
              <label
                htmlFor="sendNewsletter"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Deliver future crossposted newsletters
              </label>
              <p className="text-sm text-muted-foreground">
                Send future crossposted newsletter to subscribers via XMTP and
                email.
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          {!!crosspostId ? (
            <>
              <Button
                variant="default"
                disabled={
                  !!previousValues?.sendNewsletter === sendNewsletter ||
                  isLoading
                }
                className="max-w-[200px]"
                onClick={handleUpdate}
              >
                Update
              </Button>

              <Button
                variant="outline"
                disabled={isLoading}
                className="max-w-[200px]"
                onClick={handleDelete}
              >
                Disconnect publication
              </Button>
            </>
          ) : (
            <Button
              type="submit"
              variant="outline"
              disabled={!url || isLoading}
              className="max-w-[200px]"
            >
              Connect publication
            </Button>
          )}
        </div>
      </form>
    </section>
  )
}
